/* eslint-disable react-hooks/rules-of-hooks */
import { lazy, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdPlayArrow } from "react-icons/md";
import { HeadersFunction, LoaderFunction, MetaFunction, redirect, useLoaderData, useNavigate, useSearchParams } from "react-router";
import TimeLineViewEvent from "~/components/event-images/TimeLineViewEvent";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import ClientOnly from "~/components/shared-components/ClentOnly";
import NodataFound from "~/components/shared-components/NoDataFound";
import TabComponent from "~/components/shared-components/TabComponent";
// import VirtuosoMasonryLayout from "~/components/shared-components/VirtuosoMasonryLayout";
import YoutubeVideoPlayer from "~/components/shared-components/YoutubeVideoPlayer";
import { useLanguageParams } from "~/hook/useLanguageParams";
import useStopCurrentRunningVideoAudio from "~/hook/useStopCurrentRunningVideoAudio";
import { ImageData } from "~/interface/commonImagesize-types";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { isValidYoutubeVideo } from "~/lib/helperFuncation/youtubeThumbnails";
import { setCurrentVideo } from "~/lib/store/slice/youtubePlayerSlice";
import { useAppDispatch } from "~/lib/store/store";
import API_PATH from "~/utils/api-routes";
import { BLUR_HASH, CACHED_PAGE, FALLBACK_IMAGE_ALT_TEXT, LANG } from "~/utils/constant";
import { computeEtagFromData } from "~/utils/helper-funcations";
import api from "~/utils/Interceptor";
import { metaService } from "~/utils/metaService";

const VirtuosoMasonryLayout = lazy(() => import("~/components/shared-components/VirtuosoMasonryLayout"));

interface loaderdata {
  timeLineEvent: any[];
  galleryEvent: subEventMedia[];
  multi_event: boolean;
  count: number;
  videoid: string;
  year: number;
  url_slug: string;
  title: string;
  selectedTab: string;
  metaTitle: string;
  metaDescription: string;
  ogImage: string | null;
}

export interface subEventMedia {
  id: number;
  type: string;
  img_url: string;
  desc: string;
  img: ImageData | null;
  name: string;
}

export const handle = {
  breadcrumb: ({ params }: any) => {
    const { lang } = params;
    return (
      <>
        <BreadCrubComponent
          breadCrub={[
            {
              path: `/event-images`,
              title: lang === LANG.english ? "Event Images" : "ઇવેન્ટ ઇમેજેસ",
            },
          ]}
        />
      </>
    );
  },
  breadcrumbSchema: (match: any) => {
    const lang = match.params.lang;
    return [
      { name: lang === LANG.english ? "Home" : "ઘર", path: "/" },
      { name: lang === LANG.english ? "Event Images" : "ઇવેન્ટ ઇમેજેસ", path: "/event-images" },
      { name: match.data?.title || match.params.url_slug, path: `/event-images/${match.params.url_slug}` },
    ];
  },
};

export const loader: LoaderFunction = async ({ params, request }) => {
  const { url_slug, year, lang } = params;
  try {
    const url = new URL(request.url);
    const selectedTab = url.searchParams.get("view");

    // Fetch sub-event data
    const subEvent = await api.get(`${API_PATH.SUB_EVENT_LIST}/${url_slug}${lang === "gu" ? "?lang=gu" : ""}`);
    const subEventResponce = subEvent.status === 200;

    const data = subEventResponce ? subEvent.data.data : null;

    const title = data?.name || "Event Images";

    const metaTitle = data?.meta_title || data?.name || "Event Images";

    const metaDescription = data?.meta_desc;

    const ogImage = subEvent.data.data.thumb_img || null;

    const DATA = {
      timeLineEvent: subEventResponce ? data?.timeline : [],
      galleryEvent: subEventResponce ? data?.all : [],
      multi_event: subEventResponce ? data?.multi_event : false,
      videoid: subEventResponce ? data?.highlight_video_id : "",
      year,
      url_slug,
      title,
      selectedTab: selectedTab === null ? "timeline" : selectedTab,
      metaTitle,
      metaDescription,
      ogImage,
    };

    const etag = computeEtagFromData(DATA);
    const ifNoneMatch = request.headers.get("If-None-Match");
    if (ifNoneMatch === etag) {
      return new Response(null, {
        status: 304,
        headers: {
          Etag: etag,
        },
      });
    }

    return Response.json(DATA, {
      headers: { ...CACHED_PAGE.EVENT_IMAGES.HEADERS, ETag: etag },
    });
  } catch (error) {
    console.error("Error fetching data:", error);
    throw redirect(`/${lang}/event-images`);
  }
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export const meta: MetaFunction = ({ data, location, params }) => {
  const pathname = location?.pathname || "/";
  const search = location?.search || "";
  const metadata = data as loaderdata;
  const lang = params.lang || "en";
  const meta = getPageMeta("eventImages", lang);

  return metaService({
    title: metadata?.metaTitle ?? meta.metaTitle,
    description: metadata?.metaDescription ?? meta.metaDescription,
    ogTitle: metadata?.metaTitle ?? meta.ogTitle,
    ogDescription: metadata?.metaDescription ?? meta.ogDescription,
    ogUrl: pathname,
    ogImg: metadata?.ogImage ?? meta.ogImage,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: `${pathname}${search ? `${search}` : ""}`,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

const EventImagePage = () => {
  const { timeLineEvent, galleryEvent, multi_event, count, videoid, year, url_slug, title, selectedTab } = useLoaderData<loaderdata>();

  const navigate = useNavigate();
  const lang = useLanguageParams();
  const { t } = useTranslation();
  const tabs = [
    {
      key: "timeline",
      label: t("event_images.timeline"),
    },
    {
      key: "gallery",
      label: t("event_images.gallery"),
    },
  ];
  const [tab, setTab] = useState(selectedTab);
  const [searchParams, setSearchParamas] = useSearchParams();
  const [isValidVideo, setIsValidVideo] = useState<boolean | null>(null);

  useEffect(() => {
    let mounted = true;
    isValidYoutubeVideo(videoid).then((result) => {
      if (mounted) setIsValidVideo(result);
    });
    return () => {
      mounted = false;
    };
  }, [videoid]);

  useEffect(() => {
    setSearchParamas({ view: tab });
  }, [tab]);

  useEffect(() => {
    if (!searchParams.get("view")) {
      const defaultView = multi_event ? "timeline" : "gallery";
      navigate(`?view=${defaultView}`, { replace: true });
    }
  }, []);

  return (
    <div className="w-full flex flex-col">
      <ClientOnly
        fallback={
          <div className="w-[50%] h-[50%] my-10 mx-auto aspect-video bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center rounded-lg relative">
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center animate-pulse">
                <MdPlayArrow className="w-8 h-8 text-white" />
              </div>
              <p className="text-gray-400 text-sm font-medium">Loading player...</p>
            </div>
          </div>
        }
      >
        {isValidVideo && videoid ? <EventHighlightVideo videoid={videoid} displayName={title} slug={url_slug} /> : null}
      </ClientOnly>

      {multi_event && (
        <div className="w-full flex justify-center items-center mb-5">
          <TabComponent tabs={tabs} selectedTab={tab} onTabChange={(tab) => setTab(tab)} queryKey={"view"} />
        </div>
      )}

      <div className="px-3 w-full mb-5">
        {multi_event && selectedTab === "timeline" ? (
          <TimeLineViewEvent subEvent={timeLineEvent} year={year} eventSlug={url_slug} />
        ) : (
          <VirtuosoMasonryLayout
            imageData={galleryEvent.map((item) => ({
              id: item.id,
              urls: item.img
                ? {
                    small: item.img.s.url,
                    medium: item.img.m.url,
                    large: item.img.l.url,
                    hd: item.img.hd.url,
                    original: item.img.og.url,
                  }
                : {
                    small: item.img_url,
                    medium: item.img_url,
                    large: item.img_url,
                    hd: item.img_url,
                    original: item.img_url,
                  },
              width: item.img ? item.img.m.w : 0,
              height: item.img ? item.img.m.h : 180,
              blur_hash: item.img ? item.img.bh : BLUR_HASH,
              alt: item.name || FALLBACK_IMAGE_ALT_TEXT,
              photoName: item.name || "",
              isPreloaded: true,
            }))}
          />
        )}
      </div>
    </div>
  );
};

const EventHighlightVideo = ({ videoid, displayName, slug }: { videoid: string; displayName: string; slug: string }) => {
  if (!videoid) return null;

  const dispatch = useAppDispatch();
  const { playCurrentVideo } = useStopCurrentRunningVideoAudio();
  useEffect(() => {
    dispatch(setCurrentVideo({ id: 0, videoId: videoid, from: `/event-images/${slug}` }));
  }, []);

  return (
    <div className="w-full flex flex-col justify-center items-center gap-6 text-center">
      <h2 className="text-[32px] text-textColor font-medium font-mokubrushMedium">Video Highlight</h2>
      <div className="w-full flex justify-center items-center mb-7">
        <div className="max-sm:w-full max-sm:aspect-video md:h-[300px] md:aspect-video flex justify-center items-center overflow-hidden rounded-xl">
          <YoutubeVideoPlayer handlePlayVideo={() => playCurrentVideo(videoid)} videoId={videoid} />
        </div>
      </div>
    </div>
  );
};

export function ErrorBoundary({ error }: { error: Error }) {
  console.error(error);
  return (
    <div className="w-full h-screen flex flex-col justify-start mt-32 p-5 gap-6 text-center">
      <NodataFound text={"No event data found for this event"} />;
    </div>
  );
}

export default EventImagePage;
